import type {
	AddInvoiceNoteResponse,
	CalculateRefundResponse,
	DuplicatePaymentLookupResponse,
	GetPaymentIntentFromStripeResponse,
	GetPaymentIntentResponse,
	PaymentIntentDto,
	PaymentIntentNote
} from 'types/payment-intent'

export type { PaymentIntentDto, PaymentIntentNote }

import { checkoutPaymentServiceAxiosClient } from './base'

const PAYMENT_INTENT_PATH = '/payment-intent'

export interface CreatePaymentIntentBody {
	amount_due: number // in dollars
	practice_account_id: string
	customer_id?: string | null
	payment_method_id?: string | null
	invoice_number?: string | null
	client_id?: string | null
	source?: 'dashboard' | null
	confirm?: boolean
	check_number?: string | null
	description?: string | null
	payment_method_options?: {
		us_bank_account?: { verification_method?: string }
		acss_debit?: {
			mandate_options?: { payment_schedule?: string; transaction_type?: string }
			verification_method?: string
		}
	}
	mandate_data?: { customer_acceptance?: { type?: string } }
	created_from_mobile?: boolean
	customer_name?: string
	customer_phone?: string
	customer_email?: string
	save_payment_method?: boolean
	last_processed_by?: string
	invoice_added_by?: string
	multiple_payment?: boolean
	auto_capture?: boolean
	no_checks?: boolean
	added_by_first?: string
	added_by_last?: string
	customer_zip?: string
	bypass_surcharge?: boolean
	recipient_id?: string | null
}

export interface CreatePaymentIntentResponse {
	success?: boolean
	data: {
		payment_intent_id: string
		payment_intent_client_secret: string
		customer_id: string
	}
}

/**
 * Creates a Stripe Payment Intent via the payment service. Handles all payment
 * method types: us_bank_account (eCheck), acss_debit (Canadian cheque), card
 * (online), and card_present (terminal).
 *
 * @param paymentMethodType - Stripe payment method type, routed as URL param
 * @param body - Payment body. `amount_due` is in dollars.
 * @returns Payment intent ID, client secret, and customer ID.
 */
export const createPaymentIntent = (
	paymentMethodType: string,
	body: CreatePaymentIntentBody
): Promise<CreatePaymentIntentResponse> =>
	checkoutPaymentServiceAxiosClient.post(`${PAYMENT_INTENT_PATH}/${paymentMethodType}`, body)

export interface CapturePaymentIntentResponse {
	success: boolean
	data: Record<string, never>
}

/**
 * Captures a previously authorized Stripe Payment Intent. Used for
 * terminal/reader transactions where capture is deferred after card collection.
 *
 * @param paymentIntentId - Stripe Payment Intent ID (`pi_...`)
 * @returns Empty success envelope.
 */
export const capturePaymentIntent = (
	paymentIntentId: string
): Promise<CapturePaymentIntentResponse> =>
	checkoutPaymentServiceAxiosClient.post(`${PAYMENT_INTENT_PATH}-capture/${paymentIntentId}`)

export const createSetupPaymentIntent = (payload: Record<string, unknown>) =>
	checkoutPaymentServiceAxiosClient.post('/setup-intent', payload)

export interface UpdatePaymentIntentBody {
	metadata?: {
		[key: string]: boolean | string | number | null
	}
	lending_at_checkout_metadata?: Record<string, unknown>
	rebate_metadata?: Record<string, unknown>
}

export interface UpdatePaymentIntentResponse {
	success: boolean
}

/**
 * Updates metadata on a Stripe Payment Intent. Syncs changes to Firestore,
 * PostgreSQL, and Stripe in parallel.
 *
 * @param paymentIntentId - Stripe Payment Intent ID (`pi_...`)
 * @param body - At least one of `metadata`, `lending_at_checkout_metadata`,
 *   or `rebate_metadata`.
 * @returns Empty success envelope.
 */
export const updatePaymentIntent = (
	paymentIntentId: string,
	body: UpdatePaymentIntentBody
): Promise<UpdatePaymentIntentResponse> =>
	checkoutPaymentServiceAxiosClient.patch(`${PAYMENT_INTENT_PATH}/${paymentIntentId}`, body)

/**
 * Retrieves the canonical Postgres payment intent record by ID. Returns the
 * full `PaymentIntentDto` envelope including embedded notes and denormalized
 * latest-charge fields. Monetary fields are in cents; timestamps are ISO 8601.
 *
 * For the Stripe-native snake_case shape, use `getPaymentIntentFromStripe`.
 *
 * @param paymentIntentId - Stripe Payment Intent ID (`pi_...`)
 * @returns `{ success, data: PaymentIntentDto }`.
 */
export const getPaymentIntent = (paymentIntentId: string): Promise<GetPaymentIntentResponse> =>
	checkoutPaymentServiceAxiosClient.get(`${PAYMENT_INTENT_PATH}/${paymentIntentId}`)

/**
 * Retrieves the Stripe-native payment intent (snake_case passthrough) by ID.
 * Backend always expands `latest_charge.refunds` and adds the legacy
 * `charges.data[0]` compat field. `invoice` is NOT expanded — callers needing
 * it should use `getInvoiceById` separately.
 *
 * Do NOT use for general PI reads — use `getPaymentIntent` instead.
 *
 * @param paymentIntentId - Stripe Payment Intent ID (`pi_...`)
 * @returns `{ success, data: Stripe.PaymentIntent }`. Monetary fields are
 *   cents; `created` is epoch seconds (Stripe convention).
 */
export const getPaymentIntentFromStripe = (
	paymentIntentId: string
): Promise<GetPaymentIntentFromStripeResponse> =>
	checkoutPaymentServiceAxiosClient.get(`${PAYMENT_INTENT_PATH}/${paymentIntentId}/stripe`)

/**
 * Appends an invoice note to the given payment intent's `notes` JSONB array.
 * The backend atomically also sets `metadata.hasNotes = 'true'` in the same
 * UPDATE — callers do NOT need a follow-up `updatePaymentIntent`.
 *
 * @param paymentIntentId - Stripe Payment Intent ID (`pi_...`)
 * @param note - Note body. Must be 1-2000 characters after server-side trim;
 *   empty / whitespace-only strings yield 400.
 * @returns `{ success, data: PaymentIntentNote }` — `created_at` (ISO 8601)
 *   and `created_by` (user email) are server-set. 201 on success.
 */
export const addInvoiceNote = (
	paymentIntentId: string,
	note: string
): Promise<AddInvoiceNoteResponse> =>
	checkoutPaymentServiceAxiosClient.post(`${PAYMENT_INTENT_PATH}/${paymentIntentId}/notes`, {
		note
	})

/**
 * Cancels a Stripe Payment Intent. Used in void flows and after a terminal reader send error.
 *
 * @param paymentIntentId - Stripe Payment Intent ID (`pi_...`)
 * @returns Empty success envelope. Rejects with 409, `error_code: 'E7011'` and the trimmed
 *   `payment_intent` when the payment already succeeded.
 */
export const cancelPaymentIntent = (paymentIntentId: string) =>
	checkoutPaymentServiceAxiosClient.post(`${PAYMENT_INTENT_PATH}/${paymentIntentId}/cancel`)

/**
 * Same-day duplicate payment lookup used to warn the user before creating a
 * potentially duplicate charge. Replaces the legacy Firestore query that read
 * `paymentIntents` directly. Backend filters to `status = 'succeeded'` and
 * `createdAt >= start of today (UTC)` and resolves to a boolean.
 *
 * Callers should treat any error as fail-open (do not block the payment) and
 * just log it — see `checkPaymentAlreadyExists` in `services/charge.ts`.
 *
 * @param pimsClientId - PIMS client ID (Firestore `metadata.clientId`).
 * @param practiceId - Stripe destination account / practice ID
 *   (Firestore `transfer_data.destination`).
 * @param amount - Amount in cents.
 * @returns `{ success, data: { exists } }`.
 */
export const getDuplicatePaymentIntent = (
	pimsClientId: string,
	practiceId: string,
	amount: number
): Promise<DuplicatePaymentLookupResponse> =>
	checkoutPaymentServiceAxiosClient.get(`${PAYMENT_INTENT_PATH}/duplicate-lookup`, {
		params: { pimsClientId, practiceId, amount }
	})

/**
 * Calculates the refund breakdown for a payment intent without executing any
 * refund. Backing endpoint for the refund modals: the surcharge to
 * refund is prorated off the stored — possibly capped — surcharge fee, so the
 * dashboard displays the backend's numbers instead of duplicating the math.
 *
 * The backend rejects an amount above the remaining refundable base with a 400
 * (`Refund amount exceeds the remaining refundable amount`), so callers should
 * only ask for amounts they have already validated against the refundable max.
 *
 * @param paymentIntentId - Stripe Payment Intent ID (`pi_...`). Must exist in
 *   the payment service's Postgres `paymentIntent` table.
 * @param refundAmountCents - Base refund amount in CENTS, excluding surcharge.
 *   Must be a non-negative integer.
 * @returns `{ success, data: { refundAmount, surchargeFeeToRefund,
 *   totalAmountToRefund } }` — all amounts in cents.
 */
export const calculateRefund = (
	paymentIntentId: string,
	refundAmountCents: number
): Promise<CalculateRefundResponse> =>
	checkoutPaymentServiceAxiosClient.post(
		`${PAYMENT_INTENT_PATH}/${paymentIntentId}/calculate-refund`,
		{ refundAmount: refundAmountCents }
	)
